import { Link, useLocation } from 'react-router-dom';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDuration } from '@/lib/format-duration';
import {
  formatCourseProgressPercent,
  meetsCourseMinCompliance,
} from '@/lib/course-duration';
import { studentProfilePath } from '@/lib/user-detail-navigation';

export interface LearningSessionTimeRow {
  userId: string;
  periodActiveSeconds: number;
  todayActiveSeconds: number;
  lastConnectionAt: string | null;
  sessionCount: number;
  user: {
    id: string;
    fullName: string;
    name: string;
    email: string;
    avatarUrl?: string | null;
  };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface LearningSessionTimeTableProps {
  records: LearningSessionTimeRow[];
  isLoading: boolean;
  page: number;
  lastPage: number;
  total: number;
  rangeStart: number;
  rangeEnd: number;
  onPageChange: (page: number) => void;
  emptyMessage?: string;
  courseTimeLabel?: string;
  courseTotalActiveSeconds?: number | null;
  academicHours?: number | string | null;
  minCompliancePercent?: number | string | null;
  embedded?: boolean;
}

export function LearningSessionTimeTable({
  records,
  isLoading,
  page,
  lastPage,
  total,
  rangeStart,
  rangeEnd,
  onPageChange,
  emptyMessage = 'No se encontraron registros de tiempo de conexión.',
  courseTimeLabel = 'Tiempo total',
  courseTotalActiveSeconds = null,
  academicHours = null,
  minCompliancePercent = null,
  embedded = false,
}: LearningSessionTimeTableProps) {
  const location = useLocation();
  const { hasAnyPermission } = usePermissions();
  const canViewDetail = hasAnyPermission(
    PERMISSIONS.PROGRESO_TIEMPO_VER,
    PERMISSIONS.PROGRESO_ADMINISTRAR,
  );
  const canViewProfile = hasAnyPermission(PERMISSIONS.USUARIOS_VER, PERMISSIONS.ESTUDIANTES_PERFIL_VER);
  const profileFrom = `${location.pathname}${location.search}`;
  const showProgress = courseTotalActiveSeconds != null && courseTotalActiveSeconds > 0;
  const colSpan = showProgress ? 8 : 7;

  const content = (
    <>
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : records.length === 0 && !embedded ? (
        <div className="lms-gradebook__table-wrap">
          <div className="lms-gradebook__empty">
            <div className="lms-gradebook__empty-icon" aria-hidden="true">
              <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                schedule
              </span>
            </div>
            <h3>Sin registros</h3>
            <p>{emptyMessage}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="lms-gradebook__table-wrap">
            <table className="lms-gradebook__table">
                <thead>
                  <tr>
                    <th className="lms-gradebook__th" scope="col">
                      Nombre completo
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      Dirección de correo
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      {courseTimeLabel}
                    </th>
                    {showProgress ? (
                      <th className="lms-gradebook__th" scope="col">
                        %
                      </th>
                    ) : null}
                    <th className="lms-gradebook__th" scope="col">
                      Hoy
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      Sesiones
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      Última conexión
                    </th>
                    <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr>
                      <td className="lms-gradebook__empty-cell" colSpan={colSpan}>
                        <div className="lms-gradebook__empty">
                          <p>{emptyMessage}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    records.map((record) => {
                      const name = record.user.fullName || record.user.name;
                      const progressLabel =
                        showProgress && courseTotalActiveSeconds != null
                          ? formatCourseProgressPercent(record.periodActiveSeconds, courseTotalActiveSeconds)
                          : null;
                      const complianceOk = meetsCourseMinCompliance(
                        record.periodActiveSeconds,
                        academicHours,
                        minCompliancePercent,
                      );
                      const progressClass =
                        complianceOk == null
                          ? 'lms-gradebook__emphasis'
                          : complianceOk
                            ? 'lms-gradebook__ok'
                            : 'lms-gradebook__bad';

                      return (
                        <tr key={record.userId}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              {record.user.avatarUrl ? (
                                <img src={record.user.avatarUrl} alt="" className="lms-gradebook__avatar" />
                              ) : (
                                <div className="lms-gradebook__avatar" aria-hidden="true">
                                  {initials(name)}
                                </div>
                              )}
                              {canViewDetail ? (
                                <Link to={`/progreso/tiempo-conexion/${record.userId}`} className="lms-gradebook__name">
                                  {name}
                                </Link>
                              ) : (
                                <span className="lms-gradebook__name">{name}</span>
                              )}
                            </div>
                          </th>
                          <td className="lms-gradebook__td">
                            <a href={`mailto:${record.user.email}`} className="lms-gradebook__mail">
                              {record.user.email}
                            </a>
                          </td>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__emphasis">{formatDuration(record.periodActiveSeconds)}</span>
                          </td>
                          {showProgress ? (
                            <td className="lms-gradebook__td">
                              <span className={progressClass}>{progressLabel ?? '—'}</span>
                            </td>
                          ) : null}
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">{formatDuration(record.todayActiveSeconds)}</span>
                          </td>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">{record.sessionCount}</span>
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                            <span className="lms-gradebook__muted">{formatDateTime(record.lastConnectionAt)}</span>
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            {canViewDetail || canViewProfile ? (
                              <div className="flex items-center justify-end gap-1">
                                {canViewDetail ? (
                                  <Link
                                    to={`/progreso/tiempo-conexion/${record.userId}`}
                                    className="lms-gradebook__action"
                                    title="Ver sesiones"
                                    aria-label={`Ver sesiones de ${name}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      visibility
                                    </span>
                                  </Link>
                                ) : null}
                                {canViewProfile ? (
                                  <Link
                                    to={studentProfilePath(record.userId, profileFrom)}
                                    className="lms-gradebook__action"
                                    title="Ver perfil"
                                    aria-label={`Ver perfil de ${name}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      person
                                    </span>
                                  </Link>
                                ) : null}
                              </div>
                            ) : (
                              <span className="lms-gradebook__muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 ? (
              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de <strong>{total}</strong>{' '}
                  resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={page <= 1}
                    onClick={() => onPageChange(Math.max(1, page - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{page}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={page >= lastPage}
                    onClick={() => onPageChange(Math.min(lastPage, page + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </>
  );

  if (embedded) return content;

  return (
    <div className="lms-gradebook lms-gradebook--forum">
      <div className="lms-course-forum-card">{content}</div>
    </div>
  );
}
